/**
 * Learning Profile Hooks
 * React Query hooks for learning profile data fetching and mutations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { CACHE_DURATIONS } from '@/lib/cache-config';
import type {
  LearningProfile,
  ProfileCourse,
  ProfileBelief,
  ProfileSummary,
  UpdateProfilePayload,
  AddCoursePayload,
  OnboardingState,
  OnboardingAnswer,
} from '@/types/learning-profile';

// User profile type (from profiles table)
interface UserProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  updated_at: string | null;
}

// Query key factory for learning profile
export const learningProfileKeys = {
  all: ['learningProfile'] as const,
  profile: (userId: string) => [...learningProfileKeys.all, 'profile', userId] as const,
  userProfile: (userId: string) => [...learningProfileKeys.all, 'userProfile', userId] as const,
  courses: (userId: string) => [...learningProfileKeys.all, 'courses', userId] as const,
  beliefs: (userId: string) => [...learningProfileKeys.all, 'beliefs', userId] as const,
  summary: (userId: string) => [...learningProfileKeys.all, 'summary', userId] as const,
  onboarding: (userId: string) => [...learningProfileKeys.all, 'onboarding', userId] as const,
};

/**
 * Fetch user's learning profile
 */
export function useLearningProfile() {
  const { user } = useAuth();

  return useQuery({
    queryKey: learningProfileKeys.profile(user?.id ?? ''),
    queryFn: async (): Promise<LearningProfile | null> => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('learning_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No profile found - this shouldn't happen with the signup trigger
          console.warn('No learning profile found for user:', user.id);
          return null;
        }
        throw error;
      }

      return data as unknown as LearningProfile;
    },
    enabled: !!user?.id,
    staleTime: CACHE_DURATIONS.USER_PROGRESS,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Fetch user's profile from profiles table (first_name, last_name, etc.)
 */
export function useUserProfile() {
  const { user } = useAuth();

  return useQuery({
    queryKey: learningProfileKeys.userProfile(user?.id ?? ''),
    queryFn: async (): Promise<UserProfile | null> => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, avatar_url, updated_at')
        .eq('id', user.id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          console.warn('No profile found for user:', user.id);
          return null;
        }
        throw error;
      }

      return data as UserProfile;
    },
    enabled: !!user?.id,
    staleTime: CACHE_DURATIONS.USER_PROGRESS,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Fetch user's current courses
 * Joins with subjects table to get the current name for linked subjects
 */
export function useProfileCourses() {
  const { user } = useAuth();

  return useQuery({
    queryKey: learningProfileKeys.courses(user?.id ?? ''),
    queryFn: async (): Promise<ProfileCourse[]> => {
      if (!user?.id) return [];

      // First get the profile id
      const { data: profile } = await supabase
        .from('learning_profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!profile) return [];

      // Join with subjects table to get linked subject name
      const { data, error } = await supabase
        .from('profile_courses')
        .select('*, subjects:subject_id(name)')
        .eq('profile_id', profile.id)
        .eq('is_current', true)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;

      // Transform the nested subject data to flat linked_subject_name
      const courses = (data ?? []).map((course: any) => ({
        ...course,
        linked_subject_name: course.subjects?.name ?? null,
        subjects: undefined, // Remove the nested object
      }));

      return courses as ProfileCourse[];
    },
    enabled: !!user?.id,
    staleTime: CACHE_DURATIONS.USER_CONTENT,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Fetch user's top beliefs (weaknesses/strengths)
 */
export function useProfileBeliefs(type?: 'weakness' | 'strength') {
  const { user } = useAuth();

  return useQuery({
    queryKey: [...learningProfileKeys.beliefs(user?.id ?? ''), type],
    queryFn: async (): Promise<ProfileBelief[]> => {
      if (!user?.id) return [];

      // First get the profile id
      const { data: profile } = await supabase
        .from('learning_profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!profile) return [];

      let query = supabase
        .from('profile_beliefs')
        .select('*')
        .eq('profile_id', profile.id)
        .eq('user_visible', true)
        .order('confidence', { ascending: false })
        .order('display_priority', { ascending: true })
        .limit(10);

      if (type) {
        query = query.eq('belief_type', type);
      }

      const { data, error } = await query;

      if (error) throw error;

      return (data ?? []) as ProfileBelief[];
    },
    enabled: !!user?.id,
    staleTime: CACHE_DURATIONS.USER_PROGRESS,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Fetch complete profile summary for dashboard
 */
export function useProfileSummary() {
  const { user } = useAuth();

  return useQuery({
    queryKey: learningProfileKeys.summary(user?.id ?? ''),
    queryFn: async (): Promise<ProfileSummary | null> => {
      if (!user?.id) return null;

      // Fetch profile
      const { data: profile, error: profileError } = await supabase
        .from('learning_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (profileError || !profile) return null;

      // Fetch courses, beliefs in parallel
      const [coursesResult, beliefsResult] = await Promise.all([
        supabase
          .from('profile_courses')
          .select('*')
          .eq('profile_id', profile.id)
          .eq('is_current', true)
          .order('created_at', { ascending: false }),
        supabase
          .from('profile_beliefs')
          .select('*')
          .eq('profile_id', profile.id)
          .eq('belief_type', 'weakness')
          .eq('user_visible', true)
          .order('confidence', { ascending: false })
          .limit(5),
      ]);

      return {
        profile: profile as unknown as LearningProfile,
        courses: (coursesResult.data ?? []) as ProfileCourse[],
        topWeaknesses: (beliefsResult.data ?? []) as ProfileBelief[],
        recentProgress: {
          sessionsThisWeek: 0, // TODO: Calculate from signals
          masteryChange: 0, // TODO: Calculate from mastery map
        },
      };
    },
    enabled: !!user?.id,
    staleTime: CACHE_DURATIONS.USER_PROGRESS,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Get onboarding state
 */
export function useOnboardingState() {
  const { data: profile, isLoading } = useLearningProfile();

  const state: OnboardingState = {
    stage: 1,
    completed: profile?.onboarding_completed ?? false,
    skipped: profile?.onboarding_skipped ?? false,
    answeredQuestions: [],
    pendingQuestions: [],
  };

  // Determine which questions have been answered
  if (profile) {
    if (profile.current_status !== 'unknown') {
      state.answeredQuestions.push('current_status');
    }
    if (profile.goal_mode !== 'general') {
      state.answeredQuestions.push('goal_mode');
    }

    // Determine stage based on what's been answered
    if (state.answeredQuestions.includes('current_status') && state.answeredQuestions.includes('goal_mode')) {
      state.stage = 2;
    }
    if (profile.target_bar_state || profile.onboarding_completed) {
      state.stage = 3;
    }
    if (profile.feedback_tone !== 'balanced' || profile.avg_session_length_minutes !== 20) {
      state.stage = 4;
    }

    // Pending questions
    if (profile.current_status === 'unknown') {
      state.pendingQuestions.push('current_status');
    }
    if (profile.goal_mode === 'general' && profile.current_status !== 'unknown') {
      state.pendingQuestions.push('goal_mode');
    }
  }

  return { state, isLoading };
}

/**
 * Update learning profile mutation
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (payload: UpdateProfilePayload): Promise<LearningProfile> => {
      if (!user?.id) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('learning_profiles')
        .update({
          ...payload,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;

      return data as unknown as LearningProfile;
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: learningProfileKeys.profile(user.id) });
        queryClient.invalidateQueries({ queryKey: learningProfileKeys.summary(user.id) });
      }
    },
  });
}

/**
 * Add course mutation
 */
export function useAddCourse() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (payload: AddCoursePayload): Promise<ProfileCourse> => {
      if (!user?.id) throw new Error('Not authenticated');

      // Get profile id
      const { data: profile } = await supabase
        .from('learning_profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!profile) throw new Error('Profile not found');

      const { data, error } = await supabase
        .from('profile_courses')
        .insert({
          profile_id: profile.id,
          subject: payload.subject,
          subject_id: payload.subject_id ?? null,
          course_name: payload.course_name ?? null,
          semester: payload.semester ?? getCurrentSemester(),
          source: 'declared',
          confidence: 1.0,
          is_current: true,
        })
        .select()
        .single();

      if (error) {
        // Handle unique constraint violation
        if (error.code === '23505') {
          throw new Error('Course already added');
        }
        throw error;
      }

      return data as ProfileCourse;
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: learningProfileKeys.courses(user.id) });
        queryClient.invalidateQueries({ queryKey: learningProfileKeys.summary(user.id) });
      }
    },
  });
}

/**
 * Remove course mutation (hard delete)
 */
export function useRemoveCourse() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (courseId: string): Promise<void> => {
      if (!user?.id) throw new Error('Not authenticated');

      // Get user's profile_id for ownership verification
      const { data: profile } = await supabase
        .from('learning_profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!profile) throw new Error('Profile not found');

      // Security: Only delete if course belongs to user's profile
      const { error } = await supabase
        .from('profile_courses')
        .delete()
        .eq('id', courseId)
        .eq('profile_id', profile.id); // Ensures user can only delete their own courses

      if (error) throw error;
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: learningProfileKeys.courses(user.id) });
        queryClient.invalidateQueries({ queryKey: learningProfileKeys.summary(user.id) });
      }
    },
  });
}

/**
 * Archive course mutation (soft delete - sets is_current to false)
 */
export function useArchiveCourse() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (courseId: string): Promise<void> => {
      if (!user?.id) throw new Error('Not authenticated');

      // Get user's profile_id for ownership verification
      const { data: profile } = await supabase
        .from('learning_profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!profile) throw new Error('Profile not found');

      // Security: Only archive if course belongs to user's profile
      const { error } = await supabase
        .from('profile_courses')
        .update({ is_current: false })
        .eq('id', courseId)
        .eq('profile_id', profile.id);

      if (error) throw error;
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: learningProfileKeys.courses(user.id) });
        queryClient.invalidateQueries({ queryKey: learningProfileKeys.summary(user.id) });
      }
    },
  });
}

/**
 * Restore archived course mutation
 */
export function useRestoreCourse() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (courseId: string): Promise<void> => {
      if (!user?.id) throw new Error('Not authenticated');

      // Get user's profile_id for ownership verification
      const { data: profile } = await supabase
        .from('learning_profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!profile) throw new Error('Profile not found');

      // Security: Only restore if course belongs to user's profile
      const { error } = await supabase
        .from('profile_courses')
        .update({ is_current: true })
        .eq('id', courseId)
        .eq('profile_id', profile.id);

      if (error) throw error;
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: learningProfileKeys.courses(user.id) });
        queryClient.invalidateQueries({ queryKey: learningProfileKeys.summary(user.id) });
      }
    },
  });
}

/**
 * Fetch user's archived courses
 * Joins with subjects table to get the current name for linked subjects
 */
export function useArchivedCourses() {
  const { user } = useAuth();

  return useQuery({
    queryKey: [...learningProfileKeys.courses(user?.id ?? ''), 'archived'],
    queryFn: async (): Promise<ProfileCourse[]> => {
      if (!user?.id) return [];

      // First get the profile id
      const { data: profile } = await supabase
        .from('learning_profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!profile) return [];

      // Join with subjects table to get linked subject name
      const { data, error } = await supabase
        .from('profile_courses')
        .select('*, subjects:subject_id(name)')
        .eq('profile_id', profile.id)
        .eq('is_current', false)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      // Transform the nested subject data to flat linked_subject_name
      const courses = (data ?? []).map((course: any) => ({
        ...course,
        linked_subject_name: course.subjects?.name ?? null,
        subjects: undefined, // Remove the nested object
      }));

      return courses as ProfileCourse[];
    },
    enabled: !!user?.id,
    staleTime: CACHE_DURATIONS.USER_CONTENT,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Clear all current courses mutation
 */
export function useClearCourses() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (): Promise<void> => {
      if (!user?.id) throw new Error('Not authenticated');

      // Get profile id
      const { data: profile } = await supabase
        .from('learning_profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!profile) throw new Error('Profile not found');

      // Mark all current courses as not current (preserve history)
      const { error } = await supabase
        .from('profile_courses')
        .update({ is_current: false })
        .eq('profile_id', profile.id)
        .eq('is_current', true);

      if (error) throw error;
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: learningProfileKeys.courses(user.id) });
        queryClient.invalidateQueries({ queryKey: learningProfileKeys.summary(user.id) });
      }
    },
  });
}

/**
 * Complete onboarding mutation
 */
export function useCompleteOnboarding() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (skipped: boolean = false): Promise<LearningProfile> => {
      if (!user?.id) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('learning_profiles')
        .update({
          onboarding_completed: !skipped,
          onboarding_skipped: skipped,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;

      return data as unknown as LearningProfile;
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: learningProfileKeys.profile(user.id) });
        queryClient.invalidateQueries({ queryKey: learningProfileKeys.summary(user.id) });
      }
    },
  });
}

/**
 * Update user profile (profiles table) mutation
 * Used for updating first_name, last_name, avatar_url, etc.
 */
export function useUpdateUserProfile() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (payload: { first_name?: string; last_name?: string | null; avatar_url?: string | null }): Promise<void> => {
      if (!user?.id) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('profiles')
        .update({
          ...payload,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: learningProfileKeys.userProfile(user.id) });
      }
    },
  });
}

/**
 * Submit onboarding answer mutation
 */
export function useSubmitOnboardingAnswer() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (answer: OnboardingAnswer): Promise<LearningProfile> => {
      if (!user?.id) throw new Error('Not authenticated');

      // Map question IDs to profile fields
      const updatePayload: UpdateProfilePayload = {};

      switch (answer.questionId) {
        case 'current_status':
          updatePayload.current_status = answer.value as UpdateProfilePayload['current_status'];
          break;
        case 'goal_mode':
          updatePayload.goal_mode = answer.value as UpdateProfilePayload['goal_mode'];
          break;
        case 'feedback_tone':
          updatePayload.feedback_tone = answer.value as UpdateProfilePayload['feedback_tone'];
          break;
        case 'target_bar_state':
          updatePayload.target_bar_state = answer.value as string;
          break;
        case 'avg_session_length':
          updatePayload.avg_session_length_minutes = parseInt(answer.value as string, 10);
          break;
        case 'bar_attempt':
          updatePayload.bar_attempt = answer.value as UpdateProfilePayload['bar_attempt'];
          break;
      }

      // Update profile
      const { data, error } = await supabase
        .from('learning_profiles')
        .update({
          ...updatePayload,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;

      // TODO: Emit signal for the evidence pipeline
      // await emitOnboardingSignal(user.id, answer);

      return data as unknown as LearningProfile;
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: learningProfileKeys.profile(user.id) });
        queryClient.invalidateQueries({ queryKey: learningProfileKeys.summary(user.id) });
      }
    },
  });
}

/**
 * Calculate profile completeness
 */
export function calculateProfileCompleteness(profile: LearningProfile, coursesCount: number): number {
  let completeness = 0;

  if (profile.current_status !== 'unknown') completeness += 0.3;
  if (profile.goal_mode !== 'general') completeness += 0.2;
  if (coursesCount > 0) completeness += 0.3;
  if (profile.feedback_tone !== 'balanced') completeness += 0.1;
  if (profile.avg_session_length_minutes !== 20) completeness += 0.1;

  return completeness;
}

/**
 * Get current semester string
 */
function getCurrentSemester(): string {
  const now = new Date();
  const month = now.getMonth();
  const year = now.getFullYear();

  // August-December = Fall, January-May = Spring, June-July = Summer
  if (month >= 7 && month <= 11) {
    return `fall_${year}`;
  } else if (month >= 0 && month <= 4) {
    return `spring_${year}`;
  } else {
    return `summer_${year}`;
  }
}
